import { clsx } from 'clsx'
import { useState } from 'react'
import { Button } from './Button'
import { Field, Input } from './Field'
import { Pencil } from './icons'

/** A pencil button for editing the text beside it. */
export function EditButton({ label, onClick, className }: { label: string; onClick: (e: React.MouseEvent) => void; className?: string }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} className={clsx('inline-flex shrink-0 p-1 text-dim transition-opacity hover:text-ink focus-visible:opacity-100', className)}>
      <Pencil size={13} />
    </button>
  )
}

/** One way to save an edit, e.g. "Update question (this client)". The first is the primary one and what Enter does. */
export type SaveAction = { label: string; onSave: (next: string) => void }

/**
 * The editing state of a line of text: an input, one or more save buttons and Cancel (Escape cancels). `note`
 * sits under the input; `extra` adds actions such as Delete.
 */
export function InlineEditForm({ value, label, labelHidden, placeholder, note, actions, onCancel, extra }: { value: string; label: string; labelHidden?: boolean; placeholder?: string; note?: string; actions: SaveAction[]; onCancel: () => void; extra?: React.ReactNode }) {
  const [draft, setDraft] = useState(value)
  const run = (action: SaveAction | undefined) => {
    const next = draft.trim()
    if (action && next) action.onSave(next)
    onCancel()
  }
  return (
    <div className="grid gap-3">
      <Field label={label} labelHidden={labelHidden} hint={note}>
        <Input
          autoFocus
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              run(actions[0])
            }
            if (e.key === 'Escape') onCancel()
          }}
        />
      </Field>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {actions.map((a, i) => (
          <Button key={a.label} size="sm" variant={i === 0 ? 'primary' : 'ghost'} arrow={false} onClick={() => run(a)} disabled={!draft.trim()}>
            {a.label}
          </Button>
        ))}
        <Button variant="link" size="sm" arrow={false} onClick={onCancel}>
          Cancel
        </Button>
        {extra}
      </div>
    </div>
  )
}

/** A line of text with a pencil beside it that swaps in InlineEditForm with a single Save. */
export function InlineEdit({ value, label, onSave, note, children }: { value: string; label: string; onSave: (next: string) => void; note?: string; children?: React.ReactNode }) {
  const [editing, setEditing] = useState(false)
  if (editing) return <InlineEditForm value={value} label={label} note={note} actions={[{ label: 'Save', onSave: (v) => v !== value && onSave(v) }]} onCancel={() => setEditing(false)} />
  return (
    <div className="flex items-center gap-2">
      {children}
      <EditButton label={`Edit “${value}”`} onClick={() => setEditing(true)} />
    </div>
  )
}
